import { useColorScheme } from "@mui/material";

import moment from "moment-timezone";
import React, { useEffect, useMemo, useState } from "react";
import { DEFAULT_UI_STEP, DEFAULT_UI_VIEW } from "../../constants";
import { useI18nLanguage } from "../../hooks/i18nLanguage";
import { useBookingForm } from "../../hooks/useBookingForm";
import { useErrorDialog } from "../../hooks/useErrorDialog";
import AccessibilityStatement from "../../pages/AccessibilityStatement";
import { useSchedulerStore } from "../../state/scheduler.store";
import { computeSelectedSlotFormatted } from "../../utils/slotTime";
import GlobalErrorDialog from "./GlobalErrorDialog";
import { MeetingDetailsDialog } from "./MeetingDetailsDialog";

function Dialogs() {
  const { ui, actions, meetingLink, sessionData } = useSchedulerStore();
  const { handleFormInput } = useBookingForm();
  const { handleOpenErrorDialog } = useErrorDialog(sessionData);
  const themeVariant = useColorScheme();
  const { selectedLanguageForMoment } = useI18nLanguage();

  // Accessibility Statement dialog state
  const [showAccessibilityStatement, setShowAccessibilityStatement] =
    useState(false);

  // Listen for custom event to open accessibility statement
  useEffect(() => {
    const handleOpenA11yStatement = () => setShowAccessibilityStatement(true);
    window.addEventListener(
      "rooster:openAccessibilityStatement",
      handleOpenA11yStatement
    );
    return () => {
      window.removeEventListener(
        "rooster:openAccessibilityStatement",
        handleOpenA11yStatement
      );
    };
  }, []);

  const currentlySelectedSlot = useMemo(() => {
    if (
      !sessionData?.selectedTimezone ||
      !sessionData?.selectedSlot ||
      sessionData?.selectedSlot.hour === null ||
      sessionData?.selectedSlot.hour === undefined ||
      sessionData?.selectedSlot.hour < 0
    ) {
      return null;
    }
    return computeSelectedSlotFormatted(
      sessionData.selectedSlot.date,
      sessionData.selectedSlot.hour,
      sessionData.selectedSlot.minute ?? 0,
      sessionData.selectedTimezone,
      selectedLanguageForMoment
    );
  }, [
    sessionData?.selectedSlot,
    sessionData?.selectedTimezone,
    selectedLanguageForMoment
  ]);

  return (
    <>
      <MeetingDetailsDialog
        open={ui?.currentStep === "form"}
        onClose={() =>
          actions.setUiState({
            ...ui,
            currentView: ui?.currentView ?? DEFAULT_UI_VIEW,
            currentStep: DEFAULT_UI_STEP
          })
        }
        currentlySelectedSlot={currentlySelectedSlot}
        meetingLink={meetingLink}
        sessionData={sessionData}
        handleFormInput={handleFormInput}
        actions={actions}
        ui={ui}
        themeVariant={themeVariant.colorScheme ?? "light"}
      />

      <GlobalErrorDialog
        open={!!ui?.error?.message}
        onClose={() => handleOpenErrorDialog(false)}
        errorMessage={ui?.error?.message}
        errorDescription={ui?.error?.description}
        errorType={ui?.error?.type}
        themeVariant={themeVariant.colorScheme ?? "light"}
      />

      <AccessibilityStatement
        open={showAccessibilityStatement}
        onClose={() => setShowAccessibilityStatement(false)}
        themeVariant={themeVariant.colorScheme ?? "light"}
      />
    </>
  );
}

export default Dialogs;
